import {
  useBoardDragPreviewStore,
  useBoardElementRectStore,
  useBoardSelectionStore,
  useWallContentStore,
  type WallBoard,
} from '@walls/store';
import { findCardById } from '../card-actions/utils/cardLookup';
import { getConnectionPosition, getRenderedConnections } from '../connections/connectionGeometry';
import { getShapeType } from '../drawing/utils/shapeAttributes';
import { isClusterCard } from '../selectors/boardCardSelectors';
import { getLineMenuPosition } from '../shapes/geometry/lineGeometry';
import { getShapeBounds } from '../shapes/geometry/shapeGeometry';
import { CardOrClusterInlineMenu } from './CardOrClusterInlineMenu';
import { ConnectionInlineMenu } from './ConnectionInlineMenu';
import { ShapeInlineMenu } from './ShapeInlineMenu';

interface InlineMenuOverlayProps {
  board: WallBoard;
}

export function InlineMenuOverlay({ board }: InlineMenuOverlayProps) {
  const isCardDragging = useBoardDragPreviewStore(
    (state) => Object.keys(state.cardDragPreviews).length > 0
  );
  const currentElement = useBoardSelectionStore((state) => state.currentElement);
  const rects = useBoardElementRectStore((state) => state.rects);
  const cards = useWallContentStore((state) => state.cards);
  const connections = useWallContentStore((state) => state.connections);
  const shapes = useWallContentStore((state) => state.shapes);

  if (isCardDragging || !currentElement) {
    return null;
  }

  if (currentElement.type === 'card' || currentElement.type === 'cluster') {
    const card = findCardById(cards, currentElement.id);
    const rect = rects[currentElement.id];

    if (!card || !rect) {
      return null;
    }

    return (
      <CardOrClusterInlineMenu
        board={board}
        card={card}
        isCluster={currentElement.type === 'cluster' || isClusterCard(card)}
        left={rect.x - 12}
        top={rect.y - 10}
      />
    );
  }

  if (currentElement.type === 'connection') {
    const renderedConnection = getRenderedConnections(cards, connections, rects).find(
      ({ connection }) => connection.id === currentElement.id
    );

    if (!renderedConnection) {
      return null;
    }

    const position = getConnectionPosition(renderedConnection.linePoints);

    return (
      <ConnectionInlineMenu
        board={board}
        connection={renderedConnection.connection}
        left={position.x - 12}
        top={position.y - 10}
      />
    );
  }

  if (
    currentElement.type !== 'shape' &&
    currentElement.type !== 'line' &&
    currentElement.type !== 'text'
  ) {
    return null;
  }

  const shape = shapes.find((currentShape) => currentShape.id === currentElement.id);

  if (!shape) {
    return null;
  }

  const shapeType = getShapeType(shape);

  if (shapeType === 'line') {
    const position = getLineMenuPosition(shape);

    return (
      <ShapeInlineMenu board={board} shape={shape} left={position.x - 12} top={position.y - 10} />
    );
  }

  const bounds = getShapeBounds(shape);
  return <ShapeInlineMenu board={board} shape={shape} left={bounds.x - 12} top={bounds.y - 10} />;
}
